import { useId, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import { Button, Checkbox, Field, Input, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type {
  PermissionSetCreateInput,
  PermissionSetDetail,
  PermissionSetSummary,
  PermissionSetUpdateInput,
} from '@/lib/types';

export interface PermissionSetFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create. Provided = edit. */
  set?: PermissionSetSummary;
  onSaved: (saved: PermissionSetDetail) => void;
}

/** Server-enforced maximums, mirrored so the control stops where the API would. */
const MAX_LENGTHS = {
  slug: 60,
  name: 120,
  description: 255,
} as const;

/** `^[a-z0-9]+(-[a-z0-9]+)*$`, checked exactly as the server checks it. */
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `sort_order` is a SMALLINT UNSIGNED. */
const MAX_SORT_ORDER = 65535;

/**
 * The four seeded sets. They are what `f_role_default_sets` hands new users, so
 * a change to one of them is a change to everybody who was — or will be —
 * provisioned with it.
 *
 * 🔴 RENAMED BY BACKEND MIGRATION 151 (2026-09-04) to match the business names
 * migration 132 had already given these sets: `viewer` → `operations`,
 * `basic-user` → `university-partnerships`, `advanced-user` → `accounts`,
 * `administrator` → `management`. `super-admin` was not renamed and is absent
 * here only because `isSystem` already covers it below.
 *
 * ⚠️ THE COMMENT THAT STOOD HERE GAVE THE WRONG REASON, AND THE REASON IS WHY
 * THIS RENAME WAS SAFE. *SUPERSEDED: "Matched by slug because that is the column
 * the mapping joins on".* It is not. `f_role_default_sets` maps a role to a
 * `permission_set_id` — an integer, behind a foreign key — and the backend joins
 * it that way at `user-permission-sets.repository.ts:155` and
 * `permissions.repository.ts:411`. Nothing anywhere resolves a permission SET by
 * slug. Measured in both repositories 2026-09-04, and migration 151 carries a
 * deploy-aborting guard asserting all five roles still resolve.
 *
 * ⛔ A MODULE slug (`reference-data`, `trackers-invoice`) IS a join key and does
 * NOT move — see `ModuleSlug` and `ADMIN_WRITE_MODULES`. Nor does a ROLE slug
 * (`viewer`, `basic_user`), which is an ENUM and a claim in every live JWT.
 * Three different kinds of slug, one of which moved.
 *
 * This set decides one thing: whether to render the warning notice below.
 * `isSystem` is the broader flag, and is what actually gates DELETE.
 */
const SEEDED_SLUGS = new Set([
  'operations',
  'university-partnerships',
  'accounts',
  'management',
]);

/**
 * The one seeded set with real staff on it today — five of them. Named in the
 * notice rather than counted live: this form never loads assignment counts, and
 * a vague "some people" reads as nobody.
 *
 * Renamed from `advanced-user` by migration 151. It is the Accounts set; the
 * five members did not move.
 */
const HEAVILY_HELD_SLUG = 'accounts';

/**
 * Every value is diffable key by key, so a PATCH can carry only what changed.
 * `sortOrder` is held as a string even though the column is numeric — a
 * half-typed number has to survive being in the box.
 */
interface FormValues {
  slug: string;
  name: string;
  description: string;
  sortOrder: string;
  isActive: boolean;
}

function toFormValues(set?: PermissionSetSummary): FormValues {
  return {
    slug: set?.slug ?? '',
    name: set?.name ?? '',
    description: set?.description ?? '',
    sortOrder: set ? String(set.sortOrder) : '',
    isActive: set?.isActive ?? true,
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS**, which is what a 422's `path` names;
 * `toFormValues()` is keyed on exactly those, so deriving the list from it
 * keeps it from drifting away from the controls. `isSystem` is correctly absent
 * — the schema is `.strict()` and refuses it, and this form never offers it.
 *
 * A path can be non-empty and still bind to nothing, and that case is the worse
 * one because it looks bindable: routed by path, the message lands on an input
 * that does not exist, disappears from the banner, and still inflates the
 * "fields need attention" count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

export function PermissionSetFormModal({
  open,
  onClose,
  set,
  onSaved,
}: PermissionSetFormModalProps): ReactElement | null {
  if (!open) return null;
  return <PermissionSetForm key={set?.id ?? 'new'} onClose={onClose} set={set} onSaved={onSaved} />;
}

function PermissionSetForm({
  onClose,
  set,
  onSaved,
}: {
  onClose: () => void;
  set?: PermissionSetSummary;
  onSaved: (saved: PermissionSetDetail) => void;
}) {
  const formId = useId();
  const firstFieldRef = useRef<HTMLElement | null>(null);
  const isEdit = set !== undefined;

  const baseline = useMemo(() => toFormValues(set), [set]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => {
      const next = values[key];
      const previous = baseline[key];
      return typeof next === 'string' && typeof previous === 'string'
        ? next.trim() !== previous.trim()
        : next !== previous;
    });
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField<K extends keyof FormValues>(key: K, next: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /*
   * The response carries `items`, `itemCount` and the audit columns where the
   * write takes five plain fields, and the schema is `.strict()`, so a loaded
   * set can never be PATCHed back wholesale — an unknown key is a 422 naming it
   * rather than a silent strip. Only changed keys go.
   *
   * Two things are deliberately never sent:
   *   - `isSystem`, which the schema does not accept at all.
   *   - any actor id; the server takes the acting user from the bearer token.
   */
  function buildPayload(): PermissionSetCreateInput | PermissionSetUpdateInput {
    if (set) {
      const payload: PermissionSetUpdateInput = {};
      for (const key of changedKeys) {
        if (key === 'slug') {
          // Unreachable while the control is disabled — kept so that reversing
          // that decision cannot quietly drop the field from the payload.
          payload.slug = values.slug.trim();
        } else if (key === 'name') {
          // Not nullable server-side: `name: null` is a 422.
          payload.name = values.name.trim();
        } else if (key === 'description') {
          // '' is coerced to null server-side, so an emptied box sends null and
          // what is stored matches what comes back on the next read.
          const description = values.description.trim();
          payload.description = description === '' ? null : description;
        } else if (key === 'sortOrder') {
          payload.sortOrder = Number(values.sortOrder.trim());
        } else {
          payload.isActive = values.isActive;
        }
      }
      return payload;
    }

    const payload: PermissionSetCreateInput = {
      slug: values.slug.trim(),
      name: values.name.trim(),
    };
    const description = values.description.trim();
    if (description !== '') payload.description = description;
    const sortOrder = values.sortOrder.trim();
    if (sortOrder !== '') payload.sortOrder = Number(sortOrder);
    // `isActive` is omitted on create: the retire control is edit-only, and a
    // set created already retired is not a state anyone asks for.
    return payload;
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    /*
     * The slug is only validated on create. On edit the control is disabled, so
     * the value is the stored one by construction, and re-checking it could
     * block an unrelated save over a slug this form never wrote.
     */
    if (!isEdit) {
      const slug = values.slug.trim();
      if (slug === '') found.slug = 'A slug is required.';
      else if (slug.length > MAX_LENGTHS.slug) found.slug = 'Must be 60 characters or fewer.';
      else if (!SLUG_PATTERN.test(slug)) {
        found.slug =
          'Use lower-case letters and numbers, with single hyphens between them — no spaces, ' +
          // A LIVE slug, not a retired one: migration 151 renamed advanced-user
          // to accounts, and an example naming a set that no longer exists sends
          // the reader looking for it.
          'capitals or underscores, and no hyphen at either end. For example university-partnerships.';
      }
    }

    const name = values.name.trim();
    if (name === '') found.name = 'A name is required.';
    else if (name.length > MAX_LENGTHS.name) found.name = 'Must be 120 characters or fewer.';

    if (values.description.trim().length > MAX_LENGTHS.description) {
      found.description = 'Must be 255 characters or fewer.';
    }

    const sortOrder = values.sortOrder.trim();
    if (sortOrder !== '') {
      if (!/^\d+$/.test(sortOrder)) {
        found.sortOrder = 'Use a whole number between 0 and 65535.';
      } else if (Number(sortOrder) > MAX_SORT_ORDER) {
        found.sortOrder = 'Must be 65535 or lower.';
      }
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    /*
     * Both, and in this order.
     *
     * A portalled modal still propagates through the REACT tree rather than the
     * DOM one, so when this form is opened from inside another modal's form the
     * submit bubbles into that outer form's handler and saves it too. That is
     * exactly how a half-filled record got created in this repo once already.
     */
    event.preventDefault();
    event.stopPropagation();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    // A PATCH with no keys is a 422. Save is already disabled when nothing has
    // changed; this is the belt to that pair of braces.
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = set
        ? await api.updatePermissionSet(set.id, payload)
        : await api.createPermissionSet(payload as PermissionSetCreateInput);
      onSaved(result.data);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A duplicate slug comes back as a 409 whose message names the slug, and
         * a 409 body carries no `details[]` — so `fieldErrors` is empty and the
         * one actionable field would go unmarked unless the status is mapped
         * onto it by hand.
         *
         * Guarded on the slug actually being in the payload: this resource's
         * other 409s all belong to DELETE and never reach this handler, but a
         * PATCH that sent no slug cannot have collided on one either, so such a
         * message belongs at form level rather than under a disabled box.
         */
        if (caught.status === 409 && 'slug' in payload) {
          setServerErrors({
            slug: caught.message || 'Another permission set already uses that slug.',
          });
        } else {
          /*
           * Split against this form's OWN field list, never against the shape
           * of the path. `unroutable` is everything no input here can carry —
           * the EMPTY-path `.strict()` and "at least one field" refusals, plus
           * any path naming a key this form does not render. Rendering it at
           * form level is the only way it is seen at all, and it is never
           * counted as highlighted.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit ? !isDirty : values.slug.trim() === '' || values.name.trim() === '';

  // `isSystem` is the flag the server acts on; the four seeded slugs are the
  // sets role provisioning hands out. Either is reason enough to warn.
  //
  // The slug arm is belt-and-braces rather than the real test — `isSystem` is
  // true on all five seeded sets — so a stale slug here under-warns rather than
  // over-warning, which is the safe direction for a notice.
  const showSystemNotice = isEdit && (set.isSystem || SEEDED_SLUGS.has(baseline.slug));

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={firstFieldRef}
      title={set ? `Edit ${set.name}` : 'New permission set'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create set'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {/*
         * Said once, plainly. *SUPERSEDED 2026-10-01: "Sets change what people
         * see in this app, not what the API allows."* Since the strict model the
         * server enforces the set on every request, so that sentence became false.
         */}
        <Notice tone="info">
          Sets decide what people can see and do — the server enforces them.
        </Notice>

        {showSystemNotice && (
          <Notice tone="warning">
            System set — renaming or retiring it affects everyone who already holds it
            {baseline.slug === HEAVILY_HELD_SLUG ? ' (five members of staff today)' : ''}. It cannot
            be deleted.
          </Notice>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              // On edit the slug box is disabled and cannot take focus, so the
              // first thing anyone can actually type into is the name.
              firstFieldRef.current =
                node?.querySelector(isEdit ? 'input[name="name"]' : 'input') ?? null;
            }}
          >
            <Field
              label="Slug"
              required={!isEdit}
              error={errors.slug}
              /*
               * EDIT ONLY, and it survives the no-helper-text rule because it
               * is a why-this-is-disabled sentence, not advice: the box below
               * is `disabled` on edit, and a bare `disabled` makes "derived",
               * "you may not" and "fixed by design" look identical.
               *
               * On CREATE the box is live and its label stands alone. A
               * uniqueness collision is not something to warn about in advance
               * — it comes back as a 409 whose own message is put on this
               * field by the submit handler below.
               */
              hint={
                isEdit
                  ? 'Fixed after creation — role provisioning maps to this slug.'
                  : undefined
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  name="slug"
                  value={values.slug}
                  disabled={isEdit}
                  maxLength={MAX_LENGTHS.slug}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  invalid={Boolean(errors.slug)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('slug', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label="Name" required error={errors.name}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  name="name"
                  value={values.name}
                  maxLength={MAX_LENGTHS.name}
                  invalid={Boolean(errors.name)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('name', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label="Description" error={errors.description}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  name="description"
                  rows={2}
                  value={values.description}
                  maxLength={MAX_LENGTHS.description}
                  invalid={Boolean(errors.description)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('description', event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Sort order" error={errors.sortOrder}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                name="sortOrder"
                // Text with a numeric mode rather than type="number": the value
                // has to survive being half-typed, and a number input hands back
                // a coerced or locale-formatted one.
                inputMode="numeric"
                value={values.sortOrder}
                placeholder="0"
                invalid={Boolean(errors.sortOrder)}
                aria-describedby={describedBy}
                onChange={(event) => setField('sortOrder', event.target.value)}
              />
            )}
          </Field>
        </div>

        {/*
         * Retiring, not disabling — and edit-only.
         *
         * `isActive: false` is how a set is taken out of use, and is the
         * documented alternative when a DELETE comes back 409. It unassigns
         * nobody. The checkbox is inverted so the label states the consequence
         * rather than the column.
         */}
        {isEdit && (
          <Checkbox
            name="isActive"
            checked={!values.isActive}
            onChange={(event) => setField('isActive', !event.target.checked)}
            label="Retire this set"
          />
        )}
      </form>
    </Modal>
  );
}
